/**
 * Per-case Questionnaires card shown on the Client Dashboard.
 *
 * Lists every applicable questionnaire for the case alongside a status
 * pill (Not started / In progress / Submitted) and an action button
 * to open the form. Used both as a standalone card on the dashboard
 * and as a sub-section of the case detail page (where it's the same
 * client-facing experience).
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import API, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle, Clock, PencilSimple, FileText, Eye, PaperPlaneTilt, XCircle } from "@phosphor-icons/react";
import QuestionnaireForm from "./QuestionnaireForm";

const STATUS_META = {
  not_started: { label: "Not sent", className: "bg-slate-100 text-slate-600" },
  assigned: { label: "Sent — awaiting client", className: "bg-blue-100 text-blue-700" },
  draft: { label: "In progress", className: "bg-amber-100 text-amber-700" },
  submitted: { label: "Submitted", className: "bg-emerald-100 text-emerald-700" },
  // Solicitor-view aggregates
  in_progress: { label: "In progress", className: "bg-amber-100 text-amber-700" },
  partial_submitted: { label: "Partial", className: "bg-amber-100 text-amber-700" },
  all_submitted: { label: "All clients submitted", className: "bg-emerald-100 text-emerald-700" },
};

export default function QuestionnaireCard({ caseId, viewerRole = "client" }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeKey, setActiveKey] = useState(null);

  const fetchList = useCallback(async () => {
    try {
      const { data } = await API.get(`/cases/${caseId}/questionnaires`);
      setItems(data.applicable || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load questionnaires");
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => { fetchList(); }, [fetchList]);

  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="questionnaires-loading">
        <div className="h-3 w-32 bg-slate-200 rounded animate-pulse" />
      </div>
    );
  }

  if (items.length === 0) return null;

  return (
    <div
      className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm"
      data-testid={`questionnaires-card-${caseId}`}
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
            {viewerRole === "client" ? "Your Questionnaires" : "Client Questionnaires"}
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            {viewerRole === "client"
              ? "Complete the forms below so your solicitor can progress your matter."
              : "Only forms you've sent are visible to the client. Send when you're ready."}
          </p>
        </div>
        {viewerRole !== "client" && items.some((q) => q.status === "not_started") && (
          <Button
            size="sm" variant="outline"
            data-testid="questionnaires-send-all"
            onClick={async () => {
              const unsent = items.filter((q) => q.status === "not_started").map((q) => q.key);
              if (!unsent.length) return;
              if (!window.confirm(`Send ${unsent.length} form${unsent.length === 1 ? "" : "s"} to all clients on this case?`)) return;
              try {
                const { data } = await API.post(`/cases/${caseId}/questionnaires/assign`, { keys: unsent });
                toast.success(`Sent ${data.keys.length} form${data.keys.length === 1 ? "" : "s"}`);
                fetchList();
              } catch (err) {
                toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send");
              }
            }}
          >
            <PaperPlaneTilt weight="bold" className="w-3.5 h-3.5 mr-1" /> Send all pending
          </Button>
        )}
      </div>
      <div className="space-y-2.5">
        {items.map((q) => {
          const meta = STATUS_META[q.status] || STATUS_META.not_started;
          const isSubmitted = q.status === "submitted";
          const isAggregateAll = q.status === "all_submitted";
          return (
            <div
              key={q.key}
              className="flex items-start gap-3 p-3 rounded-md border border-slate-200 hover:border-slate-300 transition-colors"
              data-testid={`questionnaire-row-${q.key}`}
            >
              <div className="w-9 h-9 rounded-md bg-[#1462a0]/10 text-[#1462a0] flex items-center justify-center shrink-0">
                {isSubmitted || isAggregateAll ? (
                  <CheckCircle weight="fill" className="w-4.5 h-4.5" />
                ) : (
                  <FileText weight="bold" className="w-4.5 h-4.5" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-slate-900">{q.label}</span>
                  <span
                    className={`text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded ${meta.className}`}
                    data-testid={`questionnaire-status-${q.key}`}
                  >
                    {meta.label}
                  </span>
                  {viewerRole !== "client" && q.total_clients > 1 && (
                    <span className="text-[10px] text-slate-500 font-medium">
                      {q.submitted_count}/{q.total_clients} clients
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-500 mt-0.5">{q.description}</p>
                <div className="flex items-center gap-3 mt-1.5 text-[11px] text-slate-500">
                  {q.estimated_minutes && !isSubmitted && (
                    <span className="inline-flex items-center gap-1">
                      <Clock weight="bold" className="w-3 h-3" /> ~{q.estimated_minutes} min
                    </span>
                  )}
                  {q.last_submitted_at && (
                    <span>Last submitted {new Date(q.last_submitted_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span>
                  )}
                </div>
              </div>
              {viewerRole === "client" ? (
                <Button
                  variant={isSubmitted ? "outline" : "default"}
                  size="sm"
                  onClick={() => setActiveKey(q.key)}
                  data-testid={`questionnaire-open-${q.key}`}
                  className={isSubmitted ? "" : "bg-[#1462a0] hover:bg-[#10507f] text-white"}
                >
                  {isSubmitted
                    ? (<><Eye weight="bold" className="w-3.5 h-3.5 mr-1" /> View</>)
                    : (<><PencilSimple weight="bold" className="w-3.5 h-3.5 mr-1" /> {q.status === "draft" ? "Resume" : "Start"}</>)}
                </Button>
              ) : (
                <div className="flex flex-col items-end gap-1">
                  {/* Solicitor-side controls. "Send to clients" is the
                      primary action for unassigned questionnaires;
                      once sent, we show a View + Unsend option (unsend
                      only removes rows that haven't been touched). */}
                  {q.status === "not_started" ? (
                    <Button
                      size="sm"
                      onClick={async () => {
                        try {
                          const { data } = await API.post(`/cases/${caseId}/questionnaires/assign`, { keys: [q.key] });
                          toast.success(`Sent to ${data.client_ids.length} client${data.client_ids.length === 1 ? "" : "s"}`);
                          fetchList();
                        } catch (err) {
                          toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send");
                        }
                      }}
                      data-testid={`questionnaire-send-${q.key}`}
                      className="bg-[#1462a0] hover:bg-[#10507f] text-white"
                    >
                      <PaperPlaneTilt weight="bold" className="w-3.5 h-3.5 mr-1" /> Send to clients
                    </Button>
                  ) : (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setActiveKey(q.key)}
                        data-testid={`questionnaire-open-${q.key}`}
                      >
                        <Eye weight="bold" className="w-3.5 h-3.5 mr-1" /> View
                      </Button>
                      {q.status === "assigned" && (
                        <button
                          className="text-[10px] text-slate-500 hover:text-rose-600 underline"
                          onClick={async () => {
                            if (!window.confirm("Unsend this questionnaire? Any partly-completed responses will be preserved.")) return;
                            try {
                              const { data } = await API.post(`/cases/${caseId}/questionnaires/${q.key}/unassign`);
                              toast.success(`Removed ${data.removed} unsent copy`);
                              fetchList();
                            } catch (err) {
                              toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to unsend");
                            }
                          }}
                          data-testid={`questionnaire-unsend-${q.key}`}
                        >
                          Unsend
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {viewerRole === "client" && activeKey && (
        <QuestionnaireForm
          open={!!activeKey}
          onOpenChange={(v) => !v && setActiveKey(null)}
          caseId={caseId}
          qKey={activeKey}
          onSubmitted={() => { fetchList(); }}
        />
      )}
      {viewerRole !== "client" && activeKey && (
        <SolicitorSubmissionsDialog
          caseId={caseId}
          qKey={activeKey}
          open={!!activeKey}
          onOpenChange={(v) => !v && setActiveKey(null)}
          onChanged={fetchList}
        />
      )}
    </div>
  );
}


/**
 * Read-only viewer for solicitors — shows every client's submitted
 * answers + a link to the PDF document for each. Fetches from the
 * /all endpoint so we render the full multi-client list.
 */
function SolicitorSubmissionsDialog({ caseId, qKey, open, onOpenChange, onChanged }) {
  // Lazy-imported inside the same file to keep tree shallow.
  const [loading, setLoading] = useState(true);
  const [defn, setDefn] = useState(null);
  const [subs, setSubs] = useState([]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get(`/cases/${caseId}/questionnaires/${qKey}/all`);
      setDefn(data.definition);
      setSubs(data.submissions || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [caseId, qKey]);

  useEffect(() => {
    if (!open) return;
    reload();
  }, [open, reload]);

  const handleReopen = async (submissionId) => {
    const reason = window.prompt(
      "Why are you reopening this questionnaire? (optional — the client will see this note in their notification.)",
      "",
    );
    if (reason === null) return; // cancelled
    try {
      await API.post(`/cases/${caseId}/questionnaires/${qKey}/reopen`, {
        submission_id: submissionId,
        reason: reason.trim() || null,
      });
      toast.success("Questionnaire reopened — the client has been notified.");
      await reload();
      onChanged?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to reopen");
    }
  };

  return (
    <SolicitorViewModal
      open={open}
      onOpenChange={onOpenChange}
      loading={loading}
      defn={defn}
      subs={subs}
      onReopen={handleReopen}
    />
  );
}

import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

function formatVal(v, type) {
  if (v === null || v === undefined || v === "") return "—";
  if (type === "checkbox") return v ? "Yes" : "No";
  if (type === "multiselect" && Array.isArray(v)) return v.length ? v.join("; ") : "—";
  if (type === "address" && typeof v === "object") {
    return [v.line1, v.line2, v.town, v.postcode].filter(Boolean).join(", ") || "—";
  }
  if (type === "currency") {
    const n = Number(v);
    return Number.isFinite(n) ? `£${n.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : String(v);
  }
  return String(v);
}

function SolicitorViewModal({ open, onOpenChange, loading, defn, subs, onReopen }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="solicitor-submissions-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit, sans-serif" }}>
            {defn?.label || "Submissions"}
          </DialogTitle>
          <DialogDescription>{defn?.description}</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-12 text-center text-sm text-slate-500">Loading…</div>
        ) : subs.length === 0 ? (
          <div className="py-12 text-center text-sm text-slate-500">No client submissions yet.</div>
        ) : (
          <div className="space-y-6">
            {subs.map((s) => (
              <div key={s.id} className="border border-slate-200 rounded-lg p-4" data-testid={`submission-${s.id}`}>
                <div className="flex items-center justify-between flex-wrap gap-2 mb-3 pb-2 border-b border-slate-100">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">{s.client_name || "Client"}</div>
                    <div className="text-[11px] text-slate-500">
                      {s.status === "submitted" && s.submitted_at
                        ? `Submitted ${new Date(s.submitted_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })} · ${new Date(s.submitted_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`
                        : "Draft"}
                      {Array.isArray(s.history) && s.history.length > 0 && (
                        <span className="ml-2 text-amber-700">· Reopened {s.history.length}×</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className={`text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded ${
                        s.status === "submitted" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
                      }`}
                    >
                      {s.status}
                    </span>
                    {s.status === "submitted" && onReopen && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onReopen(s.id)}
                        data-testid={`reopen-submission-${s.id}`}
                        className="text-xs h-7"
                      >
                        Reopen for client
                      </Button>
                    )}
                  </div>
                </div>
                {defn?.sections?.map((sec) => {
                  const rows = sec.fields
                    .filter((f) => {
                      const gate = f.show_if;
                      if (gate && s.answers?.[gate.key] !== gate.equals) return false;
                      const v = s.answers?.[f.key];
                      if (!f.required) {
                        if (f.type === "checkbox" && !v) return false;
                        if (v === undefined || v === null || v === "") return false;
                      }
                      return true;
                    });
                  if (rows.length === 0) return null;
                  return (
                    <div key={sec.title} className="mb-3">
                      <div className="text-[11px] uppercase tracking-wide font-bold text-slate-500 mb-1">{sec.title}</div>
                      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5">
                        {rows.map((f) => (
                          <div key={f.key} className="text-xs">
                            <dt className="text-slate-500">{f.label}</dt>
                            <dd className="text-slate-900 font-medium whitespace-pre-wrap break-words">{formatVal(s.answers?.[f.key], f.type)}</dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
